import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axiosInstance from 'API/axiosInstance';
import { API_ENDPOINTS } from 'config/api';
import { decryptText } from 'config/encryption';

// Get staffId from localStorage
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null;
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return null;
};

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [screenRights, setScreenRights] = useState({});
  const [loading, setLoading] = useState(true);
  const [staffId, setStaffId] = useState(null);

  // Load staff ID and fetch permissions
  useEffect(() => {
    const staffIdValue = getAdminSession();
    setStaffId(staffIdValue);
    
    if (staffIdValue) {
      loadPermissions(staffIdValue);
    } else {
      setLoading(false);
    }
  }, []);

  const loadPermissions = async (staffIdValue) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_STAFF_SCREEN_RIGHTS(staffIdValue));
      
      if (response.data && Array.isArray(response.data)) {
        const rightsMap = {};
        response.data.forEach((right) => {
          // Handle both camelCase (JSON default) and PascalCase (if preserved)
          const hasAccess = right.hasAccess !== undefined ? right.hasAccess : right.HasAccess;
          const screenCode = right.screenCode || right.ScreenCode;
          
          if (hasAccess && screenCode) {
            rightsMap[screenCode] = {
              hasAccess: hasAccess,
              canView: right.canView !== undefined ? right.canView : right.CanView,
              canAdd: right.canAdd !== undefined ? right.canAdd : right.CanAdd,
              canEdit: right.canEdit !== undefined ? right.canEdit : right.CanEdit,
              canDelete: right.canDelete !== undefined ? right.canDelete : right.CanDelete,
              screenName: right.screenName || right.ScreenName,
            };
          }
        });
        setScreenRights(rightsMap);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setScreenRights({});
    } finally {
      setLoading(false);
    }
  };

  const checkPermission = useCallback((screenCode, permission = 'hasAccess') => {
    if (!screenCode) return false;
    const right = screenRights[screenCode];
    if (!right) return false;
    
    switch (permission) {
      case 'view':
      case 'canView':
        return right.canView;
      case 'add':
      case 'canAdd':
        return right.canAdd;
      case 'edit':
      case 'canEdit':
        return right.canEdit;
      case 'delete':
      case 'canDelete':
        return right.canDelete;
      case 'access':
      case 'hasAccess':
      default:
        return right.hasAccess;
    }
  }, [screenRights]);

  const hasAccess = useCallback((screenCode) => {
    return checkPermission(screenCode, 'hasAccess');
  }, [checkPermission]);

  const canView = useCallback((screenCode) => {
    return checkPermission(screenCode, 'view');
  }, [checkPermission]);

  const canAdd = useCallback((screenCode) => {
    return checkPermission(screenCode, 'add');
  }, [checkPermission]);

  const canEdit = useCallback((screenCode) => {
    return checkPermission(screenCode, 'edit');
  }, [checkPermission]);

  const canDelete = useCallback((screenCode) => {
    return checkPermission(screenCode, 'delete');
  }, [checkPermission]);

  const refreshPermissions = useCallback(() => {
    if (staffId) {
      loadPermissions(staffId);
    }
  }, [staffId]);

  const value = {
    screenRights,
    loading,
    staffId,
    checkPermission,
    hasAccess,
    canView,
    canAdd,
    canEdit,
    canDelete,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

PermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

